<template>
  <div id="ws-search-container">
    <form class="uk-width-1-1 uk-grid-collapse ws-search-form" action="/search" uk-grid>
      <div class="uk-width-auto"><i class="uk-search-icon fa-regular fa-magnifying-glass"></i></div>
      <div class="uk-width-expand">
        <input id="searchToggle" v-model="searchTerm" name="term" class="uk-width-1-1 uk-search-input ws-search-input" @input="fetchSearchResults" @keydown="onKeydown" type="search"
            :placeholder="'SUCHEN'" autocomplete="off" autofocus
            role="combobox" aria-autocomplete="list" aria-controls="ws-search-ac"
            :aria-expanded="isDropOpen ? 'true' : 'false'" :aria-activedescendant="activeDescendantId"/>
      </div>
    </form>
  </div>
  <div id="ws-search-ac" role="listbox" class="uk-dropbar uk-dropbar-top uk-padding-remove-bottom uk-padding-remove-top" uk-drop="boundary: true; stretch: x; boundary-x: !.ws-nav-container-desktop; flip: false; offset: 21; mode: hover">
    <div v-for="(item, key) in searchResultList" :key="item.id"
        role="option" :id="itemId(key)" :aria-selected="key === activeIndex"
        :class="{'ws-search-ac-item-active': key === activeIndex}" @mouseenter="activeIndex = key">
      <div class="ws-search-ac-item-container uk-grid-medium uk-position-relative uk-cover-container uk-margin-small-bottom uk-margin-small-top" uk-grid>
        <div class="uk-width-auto ws-dropdown-item-image" v-html="item.imageHtml"></div>
        <div class="uk-width-expand">
          <div class="uk-margin-remove-bottom uk-light uk-h2">
            <span v-html="item.name"></span><span v-if="item.articleNumber">&nbsp;|&nbsp;<span v-html="item.articleNumber"></span></span>
          </div>
          <div v-if="item.descShort" v-html="item.descShort" class="uk-text-small"></div>
        </div>
        <a v-if="item.link" :href="item.link" class="uk-position-cover" :title="item.name"></a>
      </div>
      <div v-if="key < (searchResultList.length -1)" class="uk-width-1-1" style="border-bottom: 2px solid rgba(255,255,255,.1)"></div>
    </div>
    <div class="ws-search-ac-more uk-padding-small uk-text-center" v-if="hasMoreResults">
        <a :href="moreResultsLink" class="uk-button uk-button-primary"
            role="option" :id="itemId(searchResultList.length)" :aria-selected="activeIndex === searchResultList.length"
            :class="{'ws-search-ac-item-active': activeIndex === searchResultList.length}"
            @mouseenter="activeIndex = searchResultList.length">Alle Ergebnisse anzeigen</a>
    </div>
  </div>
</template>

<script>
import ApiService from "../service/ApiService";
import {drop} from "uikit";
import {NO_SELECTION, keydownAction} from "../features/AutocompleteKeyboardNav.mjs";

export default {
  name: "SearchAutocompleteDesktop",
  data() {
    return {
      searchTerm: '',
      searchResultList: [],
      hasMoreResults: false,
      timer: null,
      logTimer: null,
      activeIndex: NO_SELECTION,
      isDropOpen: false,
    }
  },
  computed: {
    // "Mehr Ergebnisse" ist der letzte navigierbare Eintrag hinter den Treffern.
    navigableCount() {
      return this.searchResultList.length + (this.hasMoreResults ? 1 : 0)
    },
    moreResultsLink() {
      return '/search?term=' + encodeURIComponent(this.searchTerm)
    },
    activeDescendantId() {
      return this.activeIndex === NO_SELECTION ? null : this.itemId(this.activeIndex)
    }
  },
  methods: {
    itemId(index) {
      return 'ws-search-ac-item-' + index
    },
    showDrop() {
      drop('#ws-search-ac').show()
      this.isDropOpen = true
    },
    hideDrop() {
      drop('#ws-search-ac').hide()
      this.isDropOpen = false
    },
    onKeydown(event) {
      const action = keydownAction(event, {activeIndex: this.activeIndex, count: this.navigableCount})
      if (action === null) {
        return
      }

      if (action.type === 'move') {
        this.activeIndex = action.index
        this.showDrop()
        this.$nextTick(() => this.scrollActiveItemIntoView())
        return
      }

      if (action.type === 'select') {
        this.openResult(action.index)
        return
      }

      if (action.type === 'close') {
        this.activeIndex = NO_SELECTION
        this.hideDrop()
      }
    },
    openResult(index) {
      if (index === this.searchResultList.length) {
        window.location.assign(this.moreResultsLink)
        return
      }

      const item = this.searchResultList[index]
      if (item && item.link) {
        window.location.assign(item.link)
      }
    },
    scrollActiveItemIntoView() {
      if (this.activeIndex === NO_SELECTION) {
        return
      }
      const container = document.getElementById('ws-search-ac')
      const element = document.getElementById(this.itemId(this.activeIndex))
      if (!container || !element) {
        return
      }

      // Am Listenanfang/-ende bis zum jeweiligen Anschlag scrollen, sonst bleibt
      // der Bereich hinter dem aktiven Eintrag (Padding, Button) angeschnitten.
      if (this.activeIndex === 0) {
        container.scrollTop = 0
        return
      }
      if (this.activeIndex === this.navigableCount - 1) {
        container.scrollTop = container.scrollHeight
        return
      }

      // Kein scrollIntoView: der angeheftete Button-Balken liegt ueber der
      // Liste und wuerde den aktiven Eintrag sonst verdecken. Seine Hoehe wird
      // gemessen statt angenommen, damit ein Umbruch des Labels mitwandert.
      const stickyBar = container.querySelector('.ws-search-ac-more')
      const stickyHeight = stickyBar ? stickyBar.offsetHeight : 0
      const containerRect = container.getBoundingClientRect()
      const elementRect = element.getBoundingClientRect()
      const bottomLimit = containerRect.bottom - stickyHeight

      if (elementRect.bottom > bottomLimit) {
        container.scrollTop += elementRect.bottom - bottomLimit
      } else if (elementRect.top < containerRect.top) {
        container.scrollTop -= containerRect.top - elementRect.top
      }
    },
    fetchSearchResults() {
      if (this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(() => {
        ApiService.getProductSearchResult({term: this.searchTerm}).then(response => {
          this.searchResultList = response.data.results
          this.hasMoreResults = response.data.hasMoreResults
          if (this.searchResultList.length > 0) {
            this.showDrop()
          }
          if (this.logTimer) {
            clearTimeout(this.logTimer)
          }
          this.logTimer = setTimeout(() => {
            this.logSearchResult(this.searchTerm, response.data.results, this.hasMoreResults)
          }, 2000)
        })

      }, 250)
    },
    logSearchResult(searchTerm, searchResultList, hasMoreResults = false) {
      ApiService.logSearchResult(searchTerm, searchResultList, hasMoreResults).then(response => {

      })
    }
  },
  watch: {
    searchResultList: {
      handler(valNew, valBefore) {
        // Neue Trefferliste: der alte Index zeigt auf ein anderes Produkt.
        this.activeIndex = NO_SELECTION
        if(valNew.length == 0) {
          this.hideDrop()
        }
      }
    }
  }

}
</script>
